import React, { useState, useRef, useEffect } from "react";
import { BsFillInfoCircleFill } from "react-icons/bs";
import CustomModal from "../modal";
import axios from "axios";
import { AmbikaVenderApi } from "../config";

const CustomerCard = ({
  details,
  onInfoClick,
  staticStatusCodes,
  productStatusCode,
  refreshAllData,
}) => {
  // State for modal and OTP
  const [showUnifiedModal, setShowUnifiedModal] = useState(false);
  const [collectedBy, setCollectedBy] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const otpInputs = useRef([]);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [receivedAmount, setReceivedAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [remark, setRemark] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Extract values from details prop
  const {
    order_no,
    customer_name,
    customer_phone,
    delivery_date,
    tot_amount,
    paid_amount,
    orderProduct = [],
    payment_type,
    remarks,
    id: orderId, // Assuming the order ID is available in details as 'id'
  } = details || {};

  const allItemsStatus8 = () => {
    return orderProduct.every((product) => product.status === 8);
  };
  // Set collected by name automatically when modal opens
  useEffect(() => {
    if (showUnifiedModal) {
      setCollectedBy(customer_name || "");
    }
  }, [showUnifiedModal, customer_name]);

  // Calculate remaining amount
  const remaining = parseFloat(tot_amount || 0) - parseFloat(paid_amount || 0);

  const sendOtp = () => {
    setIsLoading(true);
    setError("");

    const ambikaUserToken = localStorage.getItem("ambikaUserToken");
    console.log(orderId);
    axios
      .post(
        `${AmbikaVenderApi}/vendor/sendOtpBaseOrder`,
        { OrderId: orderId },
        { headers: { Authorization: `Bearer ${ambikaUserToken}` } }
      )
      .then((res) => {
        setIsLoading(false);
        setOtpSent(true);
      })
      .catch((err) => {
        setIsLoading(false);
        setError("Failed to send OTP. Please try again.");
        console.error("OTP send error:", err);
      });
  };

  const verifyOtp = () => {
    const enteredOtp = otp.join("");
    if (enteredOtp.length !== 6) {
      setError("Please enter a valid 6-digit OTP");
      return;
    }

    setIsLoading(true);
    setError("");

    const ambikaUserToken = localStorage.getItem("ambikaUserToken");

    axios
      .post(
        `${AmbikaVenderApi}/vendor/verifyOrderOtp`,
        {
          OrderId: orderId,
          otp: enteredOtp,
        },
        { headers: { Authorization: `Bearer ${ambikaUserToken}` } }
      )
      .then((res) => {
        setIsLoading(false);
        console.log(res);
        if (res.data.success) {
          setOtpVerified(true);
        } else {
          setError(res.data.message || "OTP verification failed");
        }
      })
      .catch((err) => {
        setIsLoading(false);
        setError("OTP verification failed. Please try again.");
        console.error("OTP verification error:", err);
      });
  };

  const finalSubmit = () => {
    setIsLoading(true);
    setError("");

    const ambikaUserToken = localStorage.getItem("ambikaUserToken");
    const ambikaUserId = localStorage.getItem("ambikaUserId");

    // Prepare the request payload
    const payload = {
      UserId: ambikaUserId,
      OrderId: orderId,
      ReceivedBy: collectedBy,
      ReceivedAmount: receivedAmount,
      PaymentType: paymentMode,
      PaymentRemarks: remark,
    };

    axios
      .post(`${AmbikaVenderApi}/vendor/receivedByCustomerOrder`, payload, {
        headers: { Authorization: `Bearer ${ambikaUserToken}` },
      })
      .then((res) => {
        setIsLoading(false);
        if (res.data.success) {
          // Success case - close modal and reset states
          setShowUnifiedModal(false);
          setOtp(["", "", "", "", "", ""]);
          setCollectedBy("");
          setOtpVerified(false);
          setOtpSent(false);
          setReceivedAmount("");
          setPaymentMode("");
          setRemark("");
          setError("");
          refreshAllData();
        } else {
          setError(res.data.message || "Payment submission failed");
        }
      })
      .catch((err) => {
        setIsLoading(false);
        setError("Payment submission failed. Please try again.");
        console.error("Payment submission error:", err);
      });
  };

  const handleOtpChange = (e, index) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) otpInputs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && index > 0 && otp[index] === "") {
      otpInputs.current[index - 1]?.focus();
    }
  };

  const handlePaymentChange = (e) => setPaymentMode(e.target.value);

  // Prepare product items for display
  const items = orderProduct.map((product) => ({
    label: `${product.productName} (${product.size_name})`,
    qty: product.product_qty,
    is_urgent: product.is_urgent,
    is_parcel: product.is_parcel,
    status: product.status,
  }));

  const getStatusIcons = (product) => {
    const icons = [];

    // Add U icon if urgent
    if (product.is_urgent === 1) {
      const urgentStatus = staticStatusCodes.find(
        (status) => status.code === "U"
      );
      if (urgentStatus) {
        icons.push(
          renderStatusIcon(urgentStatus.color_code, "U", urgentStatus.name)
        );
      }
    }

    // Add P icon if parcel
    if (product.is_parcel === 1) {
      const parcelStatus = staticStatusCodes.find(
        (status) => status.code === "P"
      );
      if (parcelStatus) {
        icons.push(
          renderStatusIcon(parcelStatus.color_code, "P", parcelStatus.name)
        );
      }
    }

    // Add status icon
    if (product.status) {
      if (typeof product.status === "string") {
        const staticStatus = staticStatusCodes.find(
          (status) => status.code === product.status
        );
        if (staticStatus) {
          icons.push(
            renderStatusIcon(
              staticStatus.color_code,
              staticStatus.code,
              staticStatus.name
            )
          );
        }
      } else if (typeof product.status === "number") {
        const productStatus = productStatusCode?.find(
          (status) => status.id === product.status
        );
        if (productStatus) {
          icons.push(
            renderStatusIcon(
              productStatus.color_code,
              productStatus.code || "",
              productStatus.name
            )
          );
        }
      }
    }

    return icons;
  };

  const renderStatusIcon = (color, code, tooltip) => {
    return (
      <div
        key={`${code}-${Math.random()}`}
        className="status-icon"
        style={{
          backgroundColor: color,
          width: "20px",
          height: "20px",
          borderRadius: "50%",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontWeight: "bold",
          fontSize: "10px",
          marginRight: "5px",
          cursor: "pointer",
        }}
        title={tooltip}
      >
        {code}
      </div>
    );
  };

  return (
    <div className="col-md-4">
      <div className="card no-border h-100">
        <div className="card-body d-flex flex-column">
          <div
            className="d-flex align-items-center"
            style={{ justifyContent: "space-between" }}
          >
            <h6 className="fw-bold mb-0 p-3">{customer_name}</h6>
            <span
              className="info-icon p-3"
              style={{ cursor: "pointer" }}
              onClick={onInfoClick}
            >
              <BsFillInfoCircleFill />
            </span>
          </div>

          <ul
            className="list-unstyled small flex-grow-1"
            style={{ padding: "0px", margin: "0px" }}
          >
            {items.map((item, i2) => (
              <li
                key={i2}
                className="d-flex justify-content-between small-list-item px-3"
              >
                <span className="icon-text-wrapper d-flex align-items-center">
                  <div className="d-flex align-items-center">
                    {getStatusIcons(item)}
                  </div>
                  {item.label}
                </span>
                <span>{item.qty}</span>
              </li>
            ))}
          </ul>

          <div className="payment-summary">
            <div>
              <strong>Total</strong>
              <br />₹{parseFloat(tot_amount || 0).toFixed(2)}
            </div>
            <div>
              <strong>Advance</strong>
              <br />₹{parseFloat(paid_amount || 0).toFixed(2)}
            </div>
          </div>
          <div className="payment-summary">
            <div>
              <small>Remaining</small>
              <br />₹{remaining.toFixed(2)}
            </div>
          </div>

          {allItemsStatus8() && (
            <button
              className="btn-full mt-auto"
              onClick={() => setShowUnifiedModal(true)}
            >
              Received by Customer
            </button>
          )}
        </div>
      </div>

      {/* Unified Modal */}
      <CustomModal
        show={showUnifiedModal}
        onClose={() => {
          setShowUnifiedModal(false);
          setOtp(["", "", "", "", "", ""]);
          setCollectedBy("");
          setOtpVerified(false);
          setOtpSent(false);
          setError("");
        }}
        title="Payment Collection"
      >
        <div style={{ flex: 1, padding: 10 }}>
          {error && <div className="alert alert-danger">{error}</div>}

          <div className="mb-1">
            <label
              htmlFor="orderRemarks"
              className="d-flex mb-1  gap-1"
              style={{ fontSize: "15px" }}
            >
              Collected by Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={` unified-input `}
              id="orderRemarks"
              placeholder="Collected by Name"
              value={collectedBy}
              onChange={(e) => setCollectedBy(e.target.value)}
              style={{ background: "#f3f3f3" }}
            />
          </div>

          {!otpSent && (
            <button
              className={`btn-action ${!collectedBy.trim() ? "disabled" : ""}`}
              disabled={!collectedBy.trim() || isLoading}
              onClick={sendOtp}
            >
              {isLoading ? "Sending..." : "Send OTP"}
            </button>
          )}

          {/* Step 2: OTP Input */}
          {otpSent && !otpVerified && (
            <>
              <p className="unified-label">Verify OTP</p>
              <div className="otp-input-container">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleOtpChange(e, index)}
                    onKeyDown={(e) => handleOtpKeyDown(e, index)}
                    ref={(el) => (otpInputs.current[index] = el)}
                    className="otp-input"
                    autoFocus={index === 0}
                  />
                ))}
              </div>
              <div className="otp-actions">
                <button
                  className={`btn-action ${
                    otp.some((d) => d === "") ? "disabled" : ""
                  }`}
                  onClick={verifyOtp}
                  disabled={otp.some((d) => d === "") || isLoading}
                >
                  {isLoading ? "Verifying..." : "Verify OTP"}
                </button>
              </div>
            </>
          )}

          {/* Step 3: Payment Info */}
          {otpVerified && (
            <>
              <div className="d-flex align-items-center justify-content-between">
                <p className="unified-label">Your Due Amount is</p>
                <strong style={{ fontSize: "17px" }}>
                  ₹{remaining.toFixed(2)}
                </strong>
              </div>
              <div className="mb-3">
                <label
                  htmlFor="orderRemarks"
                  className="d-flex mb-1 gap-1"
                  style={{ fontSize: "15px" }}
                >
                  Received Amount <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={` unified-input `}
                  id="orderRemarks"
                  placeholder="Enter Received Amount"
                  value={receivedAmount}
                  onChange={(e) => setReceivedAmount(e.target.value)}
                  style={{ background: "#f3f3f3" }}
                />
              </div>
              <div className="mb-3">
                <label
                  htmlFor="orderRemarks"
                  className="d-flex mb-1  gap-1"
                  style={{ fontSize: "15px" }}
                >
                  Payment Mode <span className="text-danger">*</span>
                </label>
                <div className="payment-options">
                  <label className="payment-option">
                    <input
                      type="radio"
                      name="payment"
                      value="cash"
                      onChange={handlePaymentChange}
                    />
                    <span>Cash</span>
                  </label>
                  <label className="payment-option">
                    <input
                      type="radio"
                      name="payment"
                      value="card"
                      onChange={handlePaymentChange}
                    />
                    <span>Card</span>
                  </label>
                  <label className="payment-option">
                    <input
                      type="radio"
                      name="payment"
                      value="paytm"
                      onChange={handlePaymentChange}
                    />
                    <span>Paytm</span>
                  </label>
                  <label className="payment-option">
                    <input
                      type="radio"
                      name="payment"
                      value="sodexo"
                      onChange={handlePaymentChange}
                    />
                    <span>Sodexo</span>
                  </label>
                  <label className="payment-option">
                    <input
                      type="radio"
                      name="payment"
                      value="due"
                      onChange={handlePaymentChange}
                    />
                    <span>Due Payment</span>
                  </label>
                </div>
              </div>

              <div className="mb-3">
                <label
                  htmlFor="orderRemarks"
                  className="d-flex mb-1 gap-1"
                  style={{ fontSize: "15px" }}
                >
                  Payment Remark <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={` unified-input `}
                  id="orderRemarks"
                  placeholder="Enter Payment Remark"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  style={{ background: "#f3f3f3" }}
                />
              </div>

              <button
                className={`btn-action ${
                  !receivedAmount || !paymentMode ? "disabled" : ""
                }`}
                onClick={finalSubmit}
                disabled={!receivedAmount || !paymentMode || isLoading}
              >
                {isLoading ? "Processing..." : "Verify Payment"}
              </button>
            </>
          )}
        </div>
      </CustomModal>
    </div>
  );
};

export default CustomerCard;
